/* eslint-disable */
import { Search as SearchIcon, Lock } from "lucide-react";
import { lazy, Profiler, Suspense, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { mindshareProfiling, profileMindshareRender } from "./services/mindsharePerformance";
import {
  Link,
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { signOut } from "./auth/cognito";
import DropdownItem from "./components/DropdownMenu";
import NoAccess from "./components/NoAccess";
import ProtectedRoute from "./components/ProtectedRoute";
import { canAccessRoute } from "./config/routeAccess";
import { SELECTOR_DASHBOARDS } from "./config/selectorDashboards";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { WatermarkProvider, WatermarkVisibility } from "./contexts/WatermarkContext";
import { SteamVideoSafetyProvider } from "./contexts/SteamVideoSafetyContext";
import { DataProvider, useGlobalData } from "./contexts/DataContext";
import { TikTokDataProvider } from "./contexts/TikTokDataContext";
import Calendar from "./pages/Calendar";
import Country from "./pages/Country";
import Tableau from "./pages/Tableau";
import Followers from "./pages/Followers";
import GameDetail from "./pages/GameDetail";
import Login from "./pages/Login";
import LoginTest from "./pages/LoginTest";
import BrandLibrary from "./pages/BrandLibrary";
import GameResearch, { ResearchAccess } from "./pages/GameResearch";
import MindSHARE from "./pages/mindSHARE";
import SelectorBackground from "./components/SelectorBackground";
import MobileMindshare from "./pages/MobileMindshare";
import MindshareGuideLink from "./components/MindshareGuideLink";
import { isMindshareEditorial } from "./components/editorial/MGEditorial";
import PageAccess from "./pages/PageAccess";
import Scorecards from "./pages/Scorecards";
import Steam from "./pages/Steam";
import Steam_debug from "./pages/Steam_debug";
import TikTok from "./experiments/tiktokViewMode/TikTokEntry";
import Twitch from "./pages/Twitch";
import Roblox from "./pages/Roblox";
import RouteAnalyticsGA from "./analytics/RouteAnalyticsGA";
import TextInputField from "./components/Fields/TextInputField";
import Search from "./pages/Search";
import SteamMobile from "./pages/SteamMobile";
import TikTokAdmin from "./pages/TikTokAdmin";
import YouTube from "./pages/YouTube";
import YouTubeAdmin from "./pages/YouTubeAdmin";
import Discord from "./pages/Discord";
import DiscordAdmin from "./pages/DiscordAdmin";
import Xbox from "./pages/Xbox";
import XboxAdmin from "./pages/XboxAdmin";
import DashboardTransition, { DashboardHomeLink } from "./components/DashboardTransition";

// Feature flag (flip to true later to re-enable Roblox)
const SHOW_ROBLOX = false;
const IdentityChamber = lazy(() => import("./experiments/identityChamber/IdentityChamber"));
const CompactLogin = lazy(() => import("./experiments/compactLogin/CompactLogin"));
const MindshareArchitecture = lazy(() => import("./pages/MindshareArchitecture"));
const TikTokArchitecture = lazy(() => import("./pages/TikTokArchitecture"));
const UserProfile = lazy(() => import("./pages/UserProfile"));
const ApiDocumentation = lazy(() => import("./pages/ApiDocumentation"));

function ExperimentalRoom({ children }) {
  const { pathname } = useLocation();
  if (process.env.NODE_ENV === "development" && pathname === "/login-cinematic-test") {
    return <Suspense fallback={<main style={{ minHeight: "100vh", background: "#07090b" }} />}><CompactLogin /></Suspense>;
  }
  if (process.env.NODE_ENV === "development" && pathname === "/profile-cinematic-test") {
    return <Suspense fallback={<main style={{ minHeight: "100vh", background: "#07090b", color: "#b9cccc", padding: 32 }} role="status">Preparing the Identity Chamber...</main>}><IdentityChamber /></Suspense>;
  }
  return children;
}

// Dropdown width (menu stays small, dropdown can be wider)
const MENU_DROPDOWN_WIDTH = 260;

// Shared “tab + dropdown” styling (match the example)
const TAB_BG = "rgba(24,24,24,0.92)";
const PANEL_BG = "rgba(24,24,24,0.92)";
const PANEL_SHADOW = "0 18px 46px rgba(0,0,0,0.65)";

/** Reusable button base so legacy buttons still match other UI */
const buttonBase = {
  background: "transparent",
  border: "1px solid #444",
  borderRadius: 4,
  padding: "8px 12px",
  color: "#fff",
  cursor: "pointer",
  fontWeight: "bold",
  fontSize: 14,
  lineHeight: 1,
  display: "inline-flex",
  alignItems: "center",
};

/** Pop-out nav link style (same box as Menu button) */
const navLinkStyle = { ...buttonBase, textDecoration: "none" };

/** Active page style */
const navLinkActive = {
  background: "#fff",
  color: "#000",
  border: "1px solid #fff",
};

function MindshareRoute() {
  const location = useLocation();
  const legacyData = new URLSearchParams(location.search).get("mindshareData") === "legacy";
  return (
    <TikTokDataProvider enabled={legacyData}>
      {mindshareProfiling() ? <Profiler id="mindshare" onRender={profileMindshareRender}><MindSHARE /></Profiler> : <MindSHARE />}
    </TikTokDataProvider>
  );
}

function GlobalSearch() {
  const [raw, setRaw] = useState("");
  const [query, setQuery] = useState("");
  const [hovered, setHovered] = useState(null);
  const [maxToShow, setMaxToShow] = useState(40);
  const [focused, setFocused] = useState(false);
  const { searchGames, loading, catalogLoading, ensureSteamCatalog } = useGlobalData();
  useEffect(() => { ensureSteamCatalog(); }, [ensureSteamCatalog]);
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setQuery(raw), 120);
    return () => clearTimeout(t);
  }, [raw]);

  const results = useMemo(
    () => (query ? searchGames(query, 1000) : []),
    [query, searchGames]
  );
  useEffect(() => setMaxToShow(40), [query]);

  const visible = results.slice(0, maxToShow);
  const hasMore = results.length > maxToShow;
  const active = focused || (query && results.length > 0);

  if (loading || catalogLoading) {
    return (
      <div style={{ position: "relative", margin: "0 auto" }}>
        <TextInputField
          value=""
          placeholder="Loading games data..."
          disabled
          width={620}
        />
      </div>
    );
  }

  return (
    <div style={{ position: "relative", margin: "0 auto" }}>
      {active && (
        <div
          onMouseDown={() => {
            setFocused(false);
            setRaw("");
            setQuery("");
          }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.80)",
            backdropFilter: "blur(2px)",
            WebkitBackdropFilter: "blur(2px)",
            zIndex: 4000,
          }}
          aria-hidden="true"
        />
      )}

      <div style={{ position: "relative", zIndex: 4001 }}>
        <SearchIcon className="mg-header-search-icon" size={18} aria-hidden="true" />
        <TextInputField
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          onFocus={() => setFocused(true)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setRaw("");
              setQuery("");
              setFocused(false);
            }
          }}
          placeholder="Search games..."
          width={620}
        />
      </div>

      {query && results.length > 0 && (
        <div
          style={{
            position: "absolute",
            top: 45,
            left: 0,
            background: "rgba(0,0,0,0.85)",
            border: "1px solid #333",
            borderRadius: 8,
            zIndex: 4001,
            width: 640,
            maxHeight: 700,
            overflowY: "auto",
            boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
          }}
        >
          {visible.map((g) => (
            <div
              key={g.app_id}
              onClick={() => {
                navigate(`/game/${g.app_id}`);
                setRaw("");
                setQuery("");
                setFocused(false);
              }}
              onMouseEnter={() => setHovered(g.app_id)}
              onMouseLeave={() => setHovered(null)}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "10px 12px",
                cursor: "pointer",
                background: hovered === g.app_id ? "#1a1a1a" : "transparent",
                borderBottom: "1px solid #222",
                gap: 12,
              }}
            >
              {g.box_art ? (
                <img
                  src={g.box_art}
                  alt=""
                  onError={(e) => (e.currentTarget.style.display = "none")}
                  style={{
                    width: 160,
                    height: 80,
                    objectFit: "cover",
                    borderRadius: 6,
                    flex: "0 0 auto",
                  }}
                />
              ) : null}
              <span style={{ color: "#fff", fontSize: 16, fontWeight: 500 }}>
                {g.name}
              </span>
            </div>
          ))}
          {hasMore && (
            <button
              onClick={() => setMaxToShow((n) => n + 60)}
              className="mg-btn"
              style={{
                width: "100%",
                background: "#111",
                color: "#ccc",
                border: "none",
                padding: "10px 12px",
                borderBottomLeftRadius: 8,
                borderBottomRightRadius: 8,
                cursor: "pointer",
              }}
            >
              Show more ({results.length - visible.length} more)
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function AuthenticatedApplication() {
  return (
      <ExperimentalRoom>
      <AuthProvider>
        <WatermarkProvider>
        <SteamVideoSafetyProvider>
        <DashboardTransition>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/logintest" element={<LoginTest />} />
          <Route path="/admin/brand" element={<ProtectedRoute path="/admin/brand"><BrandLibrary /></ProtectedRoute>} />
          <Route path="/admin/architecture/mindshare" element={<ProtectedRoute path="/admin/architecture/mindshare"><Suspense fallback={<main role="status">Loading system guide...</main>}><MindshareArchitecture /></Suspense></ProtectedRoute>} />
          <Route path="/admin/architecture/tiktok" element={<ProtectedRoute path="/admin/architecture/tiktok"><Suspense fallback={<main role="status">Loading system guide...</main>}><TikTokArchitecture /></Suspense></ProtectedRoute>} />
          <Route path="/brand/mindgame/preview.html" element={<ProtectedRoute path="/admin/brand"><Navigate to="/admin/brand" replace /></ProtectedRoute>} />
          <Route path="/logintest/game/:gameId" element={<ResearchAccess><GameResearch /></ResearchAccess>} />
          <Route
            path="/*"
            element={
              <DataProvider>
                  <App />
              </DataProvider>
            }
          />
        </Routes>
        </DashboardTransition>
        </SteamVideoSafetyProvider>
        <WatermarkVisibility>
        <div
          aria-hidden="true"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 20000,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(min(360px, 100%), 1fr))",
            gridAutoRows: 220,
            alignItems: "center",
            justifyItems: "center",
            overflow: "hidden",
            pointerEvents: "none",
          }}
        >
          {Array.from({ length: 30 }, (_, index) => (
            <span
              key={index}
              style={{
                color: "rgba(220,226,232,0.10)",
                font: "700 16px/1 'Segoe UI', sans-serif",
                whiteSpace: "nowrap",
                transform: "rotate(-27deg)",
                userSelect: "none",
              }}
            >
              CAUTION: BETA TEST PRODUCT
            </span>
          ))}
        </div>
        </WatermarkVisibility>
        </WatermarkProvider>
      </AuthProvider>
      </ExperimentalRoom>
  );
}

function AppWrapper() {
  return (
    <Router>
      <Routes>
        <Route path="/API" caseSensitive element={
          <Suspense fallback={<main style={{ minHeight: "100vh", background: "#000", color: "#c0cccf", padding: 32 }} role="status">Loading API documentation...</main>}>
            <ApiDocumentation />
          </Suspense>
        } />
        <Route path="/tableau" element={<Tableau />} />
        <Route path="/*" element={<AuthenticatedApplication />} />
      </Routes>
    </Router>
  );
}

function App() {
  // Hover popdown menu
  const [menuOpen, setMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const { error: dataError } = useGlobalData();
  const { currentUser, loadingUser } = useAuth();
  const location = useLocation();
  const isSteamMobile = location.pathname === "/steam-mobile";
  const animateHeader = location.state?.fromBlades === true;
  const animateBrand = animateHeader || location.pathname.startsWith("/game/");
  const headerRef = useRef(null);
  useLayoutEffect(() => {
    const header = headerRef.current;
    if (!header || !animateHeader || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const animations = [...header.querySelectorAll(
      ":scope > :not(.mg-app-header-brand), :scope > .mg-app-header-brand > :not(a)"
    )].map(element => element.animate(
      [{ opacity: 0 }, { opacity: 1 }],
      { duration: 1465, delay: 420, easing: "ease-out", fill: "backwards" }
    ));
    return () => animations.forEach(animation => animation.cancel());
  }, [location.key, animateHeader]);
  // GlobalSearch is Steam metadata only - keep it off every other dashboard.
  const showGlobalSearch =
    ["/steam_debug", "/followers", "/search"].includes(location.pathname) ||
    location.pathname.startsWith("/game/");
  const navigate = useNavigate();

  // menu hover highlight (temporary highlight while hovering)
  const [menuHoverTo, setMenuHoverTo] = useState(null);

  // menu hover wrapper + close delay (prevents flicker)
  const menuWrapRef = useRef(null);
  const menuCloseTimerRef = useRef(null);

  const openMenu = () => {
    if (menuCloseTimerRef.current) clearTimeout(menuCloseTimerRef.current);
    setMenuOpen(true);
  };

  const closeMenuSoon = () => {
    if (menuCloseTimerRef.current) clearTimeout(menuCloseTimerRef.current);
    menuCloseTimerRef.current = setTimeout(() => {
      setMenuOpen(false);
      setMenuHoverTo(null);
    }, 140);
  };

  const closeMenuNow = () => {
    if (menuCloseTimerRef.current) clearTimeout(menuCloseTimerRef.current);
    setMenuOpen(false);
    setMenuHoverTo(null);
  };

  // Hide chrome on /login
  const isLogin = location.pathname === "/login";

  // Route table
  const routesBase = [
    { path: "/steam", element: <Steam /> },
    { path: "/steam_debug", element: <Steam_debug /> },
    { path: "/mindshare", element: <MindshareRoute /> },
    { path: "/mobile", element: <MobileMindshare /> },
    { path: "/calendar", element: <Calendar /> },
    { path: "/tiktok", element: <TikTokDataProvider dashboard><TikTok /></TikTokDataProvider> },
    { path: "/twitch", element: <Twitch /> },
    { path: "/roblox", element: <Roblox /> },
    { path: "/game/:app_id", element: <TikTokDataProvider><GameDetail /></TikTokDataProvider> },
    { path: "/followers", element: <Followers /> },
    { path: "/scorecards", element: <Scorecards /> },
    { path: "/country", element: <Country /> },
    { path: "/page-access", element: <PageAccess /> },
    { path: "/profile", element: <Suspense fallback={<main role="status">Preparing the Identity Chamber...</main>}><UserProfile /></Suspense> },
    { path: "/search", element: <TikTokDataProvider><Search /></TikTokDataProvider> },
    { path: "/admin/tiktok", element: <TikTokAdmin /> },
    { path: "/admin/youtube", element: <YouTubeAdmin /> },
    { path: "/youtube", element: <YouTube /> },
    { path: "/discord", element: <Discord /> },
    { path: "/admin/discord", element: <DiscordAdmin /> },
    { path: "/xbox", element: <Xbox /> },
    { path: "/admin/xbox", element: <XboxAdmin /> },
  ];

  // Apply feature flag
  const routes = useMemo(
    () =>
      SHOW_ROBLOX ? routesBase : routesBase.filter((r) => r.path !== "/roblox"),
    []
  );

  useEffect(() => {
    setIsAdmin(!!currentUser?.is_admin);
  }, [currentUser]);

  useEffect(() => {
    if (localStorage.getItem("admin_mfa_started") === "true" && !isLogin) {
      signOut();
      localStorage.removeItem("admin_mfa_started");
    }
  }, [isLogin]);

  const canAccess = (path) => canAccessRoute(currentUser, path);

  const renderFallbackRoute = () => {
    if (loadingUser) return null;
    if (currentUser) return <NoAccess type="404" />;
    return <Navigate to="/login" replace />;
  };

  if (dataError) {
    return (
      <div
        style={{
          height: "calc(var(--mg-vh) * 100)",
          background: "#000",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          color: "#fff",
          padding: 20,
        }}
      >
        <div style={{ color: "#ff4444", fontSize: 20, marginBottom: 10 }}>
          ❌ Failed to Load Data
        </div>
        <div
          style={{
            color: "#666",
            fontSize: 14,
            textAlign: "center",
            maxWidth: 500,
          }}
        >
          {dataError}
        </div>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: 20,
            padding: "10px 20px",
            background: "#3fa9f5",
            border: "none",
            borderRadius: 6,
            color: "#000",
            fontWeight: "bold",
            cursor: "pointer",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  // Menu items ONLY (logos from /public)
  const centerMenuOptions = useMemo(
    () => {
      const logos = {
        "/steam": "/Steam Logo.png", "/tiktok": "/TikTok Logo.png",
        "/twitch": "/Twitch Logo.png", "/discord": "/Discord Logo.png",
        "/youtube": "/YouTube Logo Header.png", "/xbox": "/Xbox Logo.png",
      };
      return SELECTOR_DASHBOARDS.map(({ route }) => route === "/mindshare"
        ? { to: route, label: "mindSHARE" } : { to: route, img: logos[route] });
    },
    []
  );

  // Which menu item is the "current page"
  const activeMenuTo = useMemo(() => {
    const p = location.pathname || "";
    if (p.startsWith("/mindshare")) return "/mindshare";
    if (p.startsWith("/steam")) return "/steam";
    if (p.startsWith("/tiktok")) return "/tiktok";
    if (p.startsWith("/twitch")) return "/twitch";
    if (p.startsWith("/calendar")) return "/calendar";
    if (p.startsWith("/discord")) return "/discord";
    if (p.startsWith("/youtube")) return "/youtube";
    if (p.startsWith("/xbox")) return "/xbox";
    return null;
  }, [location.pathname]);

  // If hovering an item, highlight it; otherwise highlight current page
  const highlightedTo = menuHoverTo || activeMenuTo;

  const [dashNavOpen, setDashNavOpen] = useState(false);
  const dashNavRef = useRef(null);

  useEffect(() => {
    setDashNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!dashNavOpen) return undefined;
    const onDown = (e) => {
      if (dashNavRef.current && !dashNavRef.current.contains(e.target)) setDashNavOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setDashNavOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [dashNavOpen]);

  // close hover menu on outside click
  useEffect(() => {
    const onDocDown = (e) => {
      if (menuWrapRef.current && !menuWrapRef.current.contains(e.target)) {
        setMenuOpen(false);
        setMenuHoverTo(null);
      }
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, []);

  // close menus on route change (safety)
  useEffect(() => {
    if (menuOpen) setMenuOpen(false);
    setMenuHoverTo(null);
  }, [location.pathname]); // eslint-disable-line

  // Esc closes menus
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (menuOpen) {
        e.preventDefault();
        closeMenuNow();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  return (
    <div
      data-app-shell
      data-mindshare={location.pathname === "/mindshare" ? "" : undefined}
      className={["/page-access", "/profile"].includes(location.pathname) ? "mg-page-access" : isMindshareEditorial(location) ? "mg-editorial" : undefined}
      style={{
        "--mg-editorial-grain": isMindshareEditorial(location)
          ? `url("${process.env.PUBLIC_URL}/redesign/images/redesign/blade_v1/art/05_global_print_grain_overlay.png")`
          : undefined,
        background: "#000",
        color: "#fff",
        minHeight: "calc(var(--mg-vh) * 100)",
        fontFamily: '"Segoe UI","Helvetica Neue","Arial",sans-serif',
      }}
    >
      {location.pathname === "/mindshare" && <SelectorBackground />}
      <RouteAnalyticsGA />
      {!isLogin && (
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Jura:wght@300;400;500;600;700&display=swap" />
      )}

      {!isLogin && !isSteamMobile && (
        <>
          <div
            ref={headerRef}
            className={`mg-app-header${dashNavOpen ? " is-dashboard-selector-open" : ""}`}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 20px",
              borderBottom: "1px solid #222",
              flexWrap: "wrap",
              gap: 12,
              position: "sticky",
              top: 0,
              zIndex: 8000,
            }}
          >
            <div className="mg-app-header-brand" style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <DashboardHomeLink style={{ cursor: "pointer" }}>
                <img
                  key={location.key}
                  src={`${process.env.PUBLIC_URL}/brand/mindgame/mindGAME-icon-color-ring${animateBrand ? `-animated.svg?entry=${encodeURIComponent(location.key)}` : ".svg"}`}
                  alt="MindGame Logo"
                  className={isMindshareEditorial(location) ? "mg-editorial-brand-mark" : undefined}
                  style={{ height: 36 }}
                />
              </DashboardHomeLink>

              {/* ✅ Menu: remove hamburger icon; caret only */}
              <div
                className="mg-legacy-menu"
                ref={menuWrapRef}
                style={{ position: "relative", display: "inline-flex" }}
                onMouseEnter={openMenu}
                onMouseLeave={closeMenuSoon}
              >
                <button
                  className="mg-btn"
                  onClick={() => setMenuOpen((v) => !v)}
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  style={{
                    background: TAB_BG,
                    border: "none",
                    borderRadius: menuOpen ? "6px 6px 0 0" : 6,
                    padding: "8px 12px",
                    color: "#fff",
                    cursor: "pointer",
                    fontWeight: "bold",
                    fontSize: 14,
                    lineHeight: 1,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    boxShadow: menuOpen
                      ? "inset 0 -1px 0 rgba(255,255,255,0.08)"
                      : "none",
                  }}
                >
                  <span>Menu</span>
                  <span
                    aria-hidden
                    style={{
                      fontSize: 12,
                      opacity: 0.85,
                      transform: "translateY(1px)",
                    }}
                  >
                    {menuOpen ? "▴" : "▾"}
                  </span>
                </button>

                {menuOpen && (
                  <div
                    role="menu"
                    onMouseEnter={openMenu}
                    onMouseLeave={() => {
                      setMenuHoverTo(null);
                      closeMenuSoon();
                    }}
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      width: MENU_DROPDOWN_WIDTH,
                      background: PANEL_BG,
                      border: "none",
                      borderRadius: "0 0 8px 8px",
                      boxShadow: PANEL_SHADOW,
                      overflow: "hidden",
                      zIndex: 10050,
                      backdropFilter: "blur(6px)",
                      WebkitBackdropFilter: "blur(6px)",
                      padding: 0,
                      marginTop: 0,
                    }}
                  >
                    {centerMenuOptions.map((opt, idx) => {
                      const locked = !canAccess(opt.to);
                      const isHighlighted = highlightedTo === opt.to;
                      const isDimmed = !isHighlighted;

                      return (
                        <button
                          key={opt.to}
                          role="menuitem"
                          disabled={locked}
                          onMouseEnter={() => {
                            if (!locked) setMenuHoverTo(opt.to);
                          }}
                          onMouseLeave={() => {
                            setMenuHoverTo(null);
                          }}
                          onClick={() => {
                            if (locked) return;
                            closeMenuNow();
                            navigate(opt.to);
                          }}
                          style={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "12px 14px",
                            background: "transparent",
                            border: "none",
                            cursor: locked ? "not-allowed" : "pointer",
                            opacity: locked ? 0.35 : 1,
                            borderBottom:
                              idx === centerMenuOptions.length - 1
                                ? "none"
                                : "1px solid rgba(255,255,255,0.07)",
                            boxShadow: isHighlighted
                              ? "inset 3px 0 0 rgba(63,169,245,0.95)"
                              : "inset 3px 0 0 rgba(0,0,0,0)",
                            transition: "box-shadow 140ms ease",
                          }}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          {opt.label ? <span className="mg-mindshare-wordmark" style={{ opacity: isDimmed ? 0.55 : 1 }}>{opt.label}</span> : <img
                            src={opt.img}
                            alt=""
                            style={{
                              height: 34,
                              width: "auto",
                              objectFit: "contain",
                              display: "block",
                              opacity: isDimmed ? 0.55 : 1,
                              filter: locked
                                ? "grayscale(1) brightness(0.75)"
                                : isDimmed
                                ? "grayscale(1) brightness(0.9)"
                                : "grayscale(0) brightness(1.15) drop-shadow(0 8px 20px rgba(0,0,0,0.6))",
                              transition:
                                "opacity 140ms ease, filter 140ms ease",
                            }}
                            onError={(e) =>
                              (e.currentTarget.style.display = "none")
                            }
                          />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            <nav
              ref={dashNavRef}
              className={`mg-dash-nav${dashNavOpen ? " is-open" : ""}`}
              aria-label="Dashboards"
            >
              <button
                type="button"
                className="mg-dash-nav-toggle"
                onClick={() => setDashNavOpen((v) => !v)}
                aria-expanded={dashNavOpen}
                aria-label={dashNavOpen ? "Collapse dashboards" : "Show all dashboards"}
              >
                <span className="mg-dash-nav-chevron" aria-hidden="true" />
              </button>
              {centerMenuOptions.map((opt, idx) => {
                const locked = !canAccess(opt.to);
                const active = activeMenuTo === opt.to;
                return (
                  <Link
                    key={opt.to}
                    to={locked ? location.pathname : opt.to}
                    aria-current={active ? "page" : undefined}
                    aria-disabled={locked}
                    aria-label={opt.to === "/mindshare" ? "mindSHARE" : opt.to.slice(1)}
                    tabIndex={locked || (!active && !dashNavOpen) ? -1 : undefined}
                    style={{ "--i": idx }}
                    onClick={(event) => {
                      if (locked) event.preventDefault();
                      else if (active) {
                        event.preventDefault();
                        setDashNavOpen((v) => !v);
                      }
                    }}
                    onMouseEnter={() => window.dispatchEvent(new CustomEvent("mindgame-dashboard-hover", { detail: { path: opt.to, open: true } }))}
                    onMouseLeave={() => window.dispatchEvent(new CustomEvent("mindgame-dashboard-hover", { detail: { path: opt.to, open: false } }))}
                    onFocus={() => window.dispatchEvent(new CustomEvent("mindgame-dashboard-hover", { detail: { path: opt.to, open: true } }))}
                    onBlur={() => window.dispatchEvent(new CustomEvent("mindgame-dashboard-hover", { detail: { path: opt.to, open: false } }))}
                    className={`mg-dashboard-tile${active ? " is-active" : ""}${locked ? " is-locked" : ""}`}
                  >
                    {opt.label ? <span className="mg-mindshare-wordmark">{opt.label}</span> : <img src={opt.img} alt="" />}
                  </Link>
                );
              })}
            </nav>
            {location.pathname === "/mindshare" && <div id="mindshare-header-controls" />}
            {location.pathname === "/steam" && <><div id="steam-header-controls" /><div id="steam-header-stats" /></>}
            {location.pathname === "/tiktok" && <div id="tiktok-header-controls" />}
            {location.pathname === "/xbox" && <div id="xbox-header-controls" />}
            {location.pathname === "/twitch" && <><div id="twitch-header-controls" /><div id="twitch-header-stats" /></>}
            </div>
            {location.pathname === "/mindshare" && <div id="mindshare-header-search"><MindshareGuideLink /></div>}
            {location.pathname === "/steam" && <div id="steam-header-search" />}
            {location.pathname === "/tiktok" && <div id="tiktok-header-search"><MindshareGuideLink dashboard="tiktok" label="TikTok" /></div>}
            {location.pathname === "/xbox" && <div id="xbox-header-search" />}
            {location.pathname === "/twitch" && <div id="twitch-header-search" />}

            <div
              className="mg-app-header-search"
              style={{
                display: "flex",
                flex: 1,
                justifyContent: "center",
                marginTop: 10,
                marginBottom: 10,
              }}
            >
              {showGlobalSearch && <GlobalSearch />}
            </div>
            {location.pathname === "/tiktok" && <div id="tiktok-header-viewfinder" />}
          </div>
        </>
      )}

      <div style={{ padding: 0 }}>
        <Routes>
          {/* Root -> steam if logged in, else login */}
          <Route
            path="/"
            element={
              loadingUser ? null : currentUser ? (
                <Navigate to="/login?menu=1" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          <Route path="/login" element={<Login />} />
          <Route path="/no-access" element={<NoAccess />} />

          {/* Mobile Steam view: requires login + Steam permission */}
          <Route path="/steam-mobile" element={<ProtectedRoute path="/steam-mobile"><SteamMobile /></ProtectedRoute>} />

          {/* All other app routes (still go through ProtectedRoute) */}
          {routes.map(({ path, element }) => (
            <Route
              key={path}
              path={path}
              element={<ProtectedRoute path={path}>{element}</ProtectedRoute>}
            />
          ))}

          <Route path="*" element={renderFallbackRoute()} />
        </Routes>
      </div>
    </div>
  );
}

export default AppWrapper;
